import { CircuitType, LevelName, PolicyMode, SmartPolicyTaskStatus, VersionLabel } from '@src/utils/enums';
import { ConnectionDto, ConnectionFwMetadata, ServiceDto } from '@src/dtos/customer.dto';
import { getServiceLevelDisplay } from '@src/utils/parse';
import { isScheduledSource, isScheduledTarget, isScheduledVersionUpcoming } from '@src/utils/scheduling-utils';
import { formatAppliedDate, formatCommonDateTime } from '@src/utils/datesUtils';
import { DRAFT_STATUSES } from '@src/utils/versionUtils';

export type PolicyStatus = 'INITIAL' | 'ACTIVE' | 'PENDING' | 'DRAFT' | 'INACTIVE';

export interface ConnectionModel {
    id: number;
    customerId: number;
    circuitId: string;
    connectionName?: string;
    type: CircuitType;
    serviceLevel: LevelName;
    plan: 'STANDARD' | 'ADVANCED' | 'PREMIUM';
    policyName?: string;
    policyStatus: VersionLabel | undefined;
    policyVersionNumber: number;
    policyVersionComments?: string;
    policyExternalId: string;
    formattedPolicyApplyDate?: string;
    policyApplyDate?: Date;
    policyApplyUser?: string;
    policyMode: PolicyMode;

    scheduleDate?: string;

    analysisDaysLeft: number;
    isInAnalysis: boolean;
    isAvailable: boolean;
    hasFailure: boolean;
    isInitial: boolean;
    isSourceSchedule: boolean;
    isTargetSchedule: boolean;
    isUpcomingSchedule: boolean;
    isScheduled: boolean,
    isFirstSmartDraft: boolean;
    isSmartDraftInProgress: boolean;
    isSelectable: boolean;
    disableReason?: string;

    isSmartEligible: boolean;
}

const calculateStatus = (sidFwMetadata: ConnectionFwMetadata, serviceLevel: LevelName): VersionLabel | undefined => {
    if (serviceLevel === LevelName.BRONZE) return undefined;
    const {versionType, versionNumber, versionStatus} = sidFwMetadata;
    if (versionType === 'DEACTIVATE') return VersionLabel.Inactive;
    if (versionNumber === 1) return VersionLabel.Initial;
    if (versionStatus === 'ACTIVE') return VersionLabel.Active;
    return versionStatus === 'PENDING' ? VersionLabel.Pending : VersionLabel.Draft;
};

const getSelectableStatus = (connection: ConnectionModel): { isSelectable: boolean, reason: string } => {
    if (connection.serviceLevel === LevelName.BRONZE) {
        return {isSelectable: false, reason: 'Selection is disabled for connections with Standard plan'};
    }
    else if (!connection.isAvailable) {
        return {isSelectable: false, reason: 'Selection is disabled for connections under maintenance'};
    }
    else if (connection.isSourceSchedule) {
        return {
            isSelectable: false,
            reason: 'Selection is disabled for connections with a scheduled source policy'
        };
    }
    else if (connection.isUpcomingSchedule) {
        return {
            isSelectable: false,
            reason: 'Selection is disabled for connections with upcoming "Scheduled Apply"'
        };
    }
    else if (connection.policyStatus === VersionLabel.Pending) {
        return {isSelectable: false, reason: 'Selection is disabled for connections with "Pending" policy'};
    }
    else if (connection.isSmartDraftInProgress) {
        return {
            isSelectable: false,
            reason: 'Selection is disabled for connections generating the Smart draft policy'
        };
    }
    return {isSelectable: true, reason: ''};
};

export const parseConnection = (connection: ConnectionDto, customerId: number): ConnectionModel => {
    const parsedConnection = {
        id: connection.serviceId,
        customerId,
        circuitId: connection.adiCircuitId,
        connectionName: connection.serviceName,
        serviceLevel: connection.serviceLevel,
        plan: getServiceLevelDisplay(connection.serviceLevel),
        isAvailable: connection.available,
        isInAnalysis: connection.smartPolicyTaskStatus === SmartPolicyTaskStatus.PENDING,
        analysisDaysLeft: connection.networkAnalysisDaysLeft,
        isSmartDraftInProgress: connection.smartPolicyTaskStatus === SmartPolicyTaskStatus.IN_PROGRESS,
        type: connection.circuitType,
        policyMode: connection.policyMode,
        ...parseSidMetadataFields(connection.sidFwMetadata, connection.serviceLevel, connection.policyMode)
    } as ConnectionModel;
    parsedConnection.isSmartEligible = parsedConnection.policyStatus !== null && parsedConnection.policyStatus !== VersionLabel.Inactive &&
        parsedConnection.policyStatus !== VersionLabel.Pending && !parsedConnection.isSmartDraftInProgress && !parsedConnection.isInAnalysis;
    const selectionStatus = getSelectableStatus(parsedConnection);
    parsedConnection.isSelectable = selectionStatus.isSelectable;
    parsedConnection.disableReason = selectionStatus.reason;
    return parsedConnection;
};

export const parseConnections = (customerId: number, connections?: ConnectionDto[]): ConnectionModel[] | undefined => {
    if (!connections) return undefined;
    return connections.map(connection => parseConnection(connection, customerId));
};

export const parseSidMetadataFields = (sidFwMetadata: ConnectionFwMetadata, serviceLevel: LevelName, policyMode: PolicyMode): Partial<ConnectionModel> => {
    const status = serviceLevel !== LevelName.BRONZE ? calculateStatus(sidFwMetadata, serviceLevel) : undefined;
    return {
        hasFailure: sidFwMetadata.latestVersionFailed,
        isFirstSmartDraft: policyMode === PolicyMode.SMART_POLICY && sidFwMetadata.versionStatus === 'DRAFT',
        isScheduled: sidFwMetadata.scheduled,
        isSourceSchedule: sidFwMetadata.scheduled && isScheduledSource(sidFwMetadata.scheduleType),
        isTargetSchedule: sidFwMetadata.scheduled && isScheduledTarget(sidFwMetadata.scheduleType),
        isUpcomingSchedule: isScheduledTarget(sidFwMetadata.scheduleType) && isScheduledVersionUpcoming(sidFwMetadata.scheduleDate),
        policyStatus: status,
        policyVersionNumber: sidFwMetadata.versionNumber,
        scheduleDate: formatCommonDateTime(sidFwMetadata.scheduleDate),
        formattedPolicyApplyDate: formatAppliedDate(sidFwMetadata.lastUpdateDateTime),
        policyApplyDate: new Date(sidFwMetadata.lastUpdateDateTime),
        policyVersionComments: sidFwMetadata.versionComments,
        policyExternalId: sidFwMetadata.externalId,
        policyApplyUser: sidFwMetadata.lastUpdaterUser,
        policyName: sidFwMetadata.versionName,
        isInitial: sidFwMetadata.versionNumber === 1 ||
            (sidFwMetadata.versionNumber === 2 && sidFwMetadata.versionStatus === 'DRAFT'),
    };
};

export const isInitialPolicyModeUndefined = (connection: ConnectionModel): boolean => {
    return connection.policyMode === PolicyMode.UNDEFINED && connection.isInitial;
};

export const isInitialPolicyModeUndefinedByService = (service: ServiceDto | ConnectionDto): boolean => {
    if (service.policyMode !== PolicyMode.UNDEFINED) return false;
    const sidFw = (service as ConnectionDto).sidFwMetadata;
    if (sidFw) {
        return sidFw.versionNumber === 1 || (sidFw.versionNumber === 2 && sidFw.versionStatus === 'DRAFT');
    } else {
        const latestVersionNumber = (service as ServiceDto).latestVersionNumber;
        const latestVersionStatus = (service as ServiceDto).latestVersionStatus;
        return latestVersionNumber === 1 || (latestVersionNumber === 2 && DRAFT_STATUSES.includes(latestVersionStatus));
    }
};